import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const { t } = useI18n();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="mx-auto max-w-md px-6 py-20 text-center" data-testid="forgot-success">
        <CheckCircle size={42} weight="fill" className="mx-auto text-primary mb-3" />
        <h1 className="font-display text-2xl font-medium mb-2">{t.forgot.sent_h1}</h1>
        <p className="text-muted-foreground leading-relaxed">{t.forgot.sent_sub}</p>
        <p className="text-xs text-muted-foreground mt-6">{t.forgot.sent_note}</p>
        <Link to="/connexion?as=pro" className="text-primary font-semibold underline text-sm mt-6 inline-block">← {t.auth.back_to_login}</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16" data-testid="forgot-page">
      <h1 className="font-display text-3xl font-medium mb-3">{t.forgot.h1}</h1>
      <p className="text-muted-foreground mb-6">{t.forgot.sub}</p>
      <form onSubmit={submit} className="space-y-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="vous@exemple.com"
          className="w-full border border-border rounded-sm px-3 py-2.5 text-sm bg-background outline-none focus:border-primary"
          data-testid="forgot-email"
        />
        <button type="submit" disabled={busy || !email} className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-3 rounded-sm font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="forgot-submit">
          {busy ? "…" : t.forgot.submit} {!busy && <ArrowRight size={14} weight="bold" />}
        </button>
        <div className="text-center">
          <Link to="/connexion?as=pro" className="text-xs text-muted-foreground hover:text-foreground">← {t.auth.back_to_login}</Link>
        </div>
      </form>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    if (!token) toast.error("Invalid link");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error(t.register.password_mismatch);
    if (pw.length < 8) return toast.error("Min 8");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw, password_confirm: pw2 });
      toast.success(t.forgot.reset_success);
      navigate("/connexion?as=pro", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16" data-testid="reset-page">
      <h1 className="font-display text-3xl font-medium mb-3">{t.forgot.reset_h1}</h1>
      <p className="text-muted-foreground mb-6">{t.forgot.reset_sub}</p>
      <form onSubmit={submit} className="space-y-4">
        <div className="relative">
          <input type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t.forgot.reset_new} minLength={8} required className="w-full border border-border rounded-sm px-3 py-2.5 pr-10 text-sm bg-background outline-none focus:border-primary" data-testid="reset-password" autoComplete="new-password" />
          <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground" aria-label={showPw ? "Masquer" : "Afficher"} data-testid="reset-password-toggle">
            {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <div className="relative">
          <input type={showPw2 ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder={t.forgot.reset_confirm} minLength={8} required className="w-full border border-border rounded-sm px-3 py-2.5 pr-10 text-sm bg-background outline-none focus:border-primary" data-testid="reset-password-confirm" autoComplete="new-password" />
          <button type="button" onClick={() => setShowPw2(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground" aria-label={showPw2 ? "Masquer" : "Afficher"} data-testid="reset-password-confirm-toggle">
            {showPw2 ? <EyeSlash size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <button type="submit" disabled={busy || !token} className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-3 rounded-sm font-semibold text-sm disabled:opacity-50" data-testid="reset-submit">
          {busy ? "…" : t.forgot.reset_submit}
        </button>
      </form>
    </div>
  );
}
